<template>
  <div :class="['card-chart', { fullscreen: dashboardFullscreen }]">
    <CardInteractive
      :title="title"
      :info="info"
      :enableFullscreen="true"
      :isLoading="isLoading"
      headerDivider
    >
      <template v-if="isLoading">
        <div class="loader-container">
          <PulseLoader :loading="true" color="#3F46D6" />
        </div>
      </template>
      <!--<template v-else-if="data?.error">
      <div class="error">
        <TextBase :text="data?.error" type="label" />
      </div>
    </template>-->
      <template v-else-if="!correctFilter">
        <div class="error">
          <TextBase v-if="correctFilter" :text="errorMessages?.NO_DATA[locale]" type="label" />
          <TextBase v-else :text="errorMessages?.INVALID_FILTER[locale]" type="label" />
        </div>
      </template>
      <template v-else>
        <TopListNew
          v-if="data"
          :data="data"
          :format="format"
          :barOptions="barOptions"
          :locale="locale"
        />
      </template>
      <template #footerControls>
        {{ t('dashboard__text__selection') }} :
        <CollapseControllerSummaryNew :showTimeSelection="true" :fullscreen="true" />
      </template>
    </CardInteractive>
  </div>
</template>

<script lang="ts" setup>
import CollapseControllerSummaryNew from './CollapseController/CollapseControllerSummaryNew.vue'
import CardInteractive from './CardInteractive.vue'
import { FormatOptions } from '../formatter'
import { useI18n } from 'vue-i18n'
import { computed, toRefs, inject } from 'vue'
import { DataSource, useDataSources } from '@/composables/useDataSources'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import TextBase from '@/design-system/atoms/TextBase.vue'
import { EntityLevel, useEntityLevel } from '@/composables/useEntityLevel'
import { storeToRefs } from 'pinia'
import { Measurement } from '@/features/dashboard'
import { MeasurementPoint } from '../measurements/contracts'
import { useComponentFilter } from '../measurements/useMeasurements'
import TopListNew from './TopListNew.vue'
import { BarConfiguration } from './toplist-types'
import { sharedDataKey } from '../../types'

type ErrorState = 'NO_DATA' | 'INVALID_FILTER'

type Props = {
  featureName: string
  title: string
  info?: string
  barOptions: BarConfiguration[]
  format: FormatOptions
  dataSource: string
  enabledEntityLevels?: EntityLevel[]
  measurements: Measurement[]
  errorMessages?: Record<ErrorState, string>
}

const props = defineProps<Props>()
const { measurements } = toRefs(props)
const sharedData = inject(sharedDataKey)
const { t } = useI18n()

useComponentFilter(props.featureName, {})
const { getDataSources } = useDataSources()
const { entityLevel } = storeToRefs(useEntityLevel())
const dataSourceIds = computed(() =>
  measurements.value.map(
    (measurement) =>
      `measurements.${props.featureName}.${measurement.name}.${measurement.field.name}.toplist.values`
  )
)
const dashboardFullscreen = computed(() => sharedData?.fullscreen ?? false)
const dataSources = getDataSources<MeasurementPoint[]>(dataSourceIds)

const mergedDataSources = computed<DataSource<MeasurementPoint[]>[]>(() => {
  return dataSources?.value
    ? Object.values(dataSources.value)
        .filter((dataSource) => dataSource !== undefined)
        .map((dataSource) => dataSource!)
    : []
})

const data = computed<MeasurementPoint[]>(() => {
  return mergedDataSources.value
    .filter((ds) => ds.data !== undefined)
    .map((ds) => ds.data!)
    .flat()
})

const isLoading = computed(() => {
  return mergedDataSources.value.every((dataSource) => dataSource.isLoading)
})

const correctFilter = computed(() => {
  if (props.enabledEntityLevels) {
    return props.enabledEntityLevels.includes(entityLevel.value)
  }
  return true
})

const { locale } = useI18n()
</script>

<style scoped lang="scss">
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
.card-chart {
  width: 100%;
}
@media screen and (min-width: 768px) {
  .card-chart {
    width: 100%;

    &.fullscreen {
      width: calc(50% - variables.$gap-large);
    }
  }
}
.container {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.error {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  width: 100%;
}
</style>
